// BSUID format used by WhatsApp username-only identities, e.g. "MX.1338488485037962"
// or "MX.ENT.1338488485037962".
const BSUID_PATTERN = /^[A-Z]{2}(\.ENT)?\.\d+$/;

// Evidence that a client really is a username-only identity, independent of the
// senderIdType label: the BSUID *shape* must match the senderId. Shape is the
// whole test — a phone and a Messenger PSID are both all digits, so neither can
// match.
//
// whatsappUserId is deliberately NOT used to DECIDE identity (neither here nor in
// the pattern fallback). It is still read as a display VALUE by getUserIdOrNull
// once identity has already been established. It is an *enrichment* field, not an
// identity assertion: on the local dump, 307 of the 309 clients carrying a
// well-formed BSUID there are ordinary phone clients (senderIdType 'phoneNumber',
// phoneNumber populated, senderId a phone). Only 2 are real username identities,
// and both also carry the BSUID in senderId. Treating that field as evidence
// would hand the userId branch to phone clients the moment backend#288 mis-stamps
// their label — the exact regression this guard exists to prevent.
//
// Corollary: a whatsappUserId copied from the senderId (also seen in the dump, on
// Messenger contacts) is irrelevant to the decision — no rule reads it to decide.
function hasUserIdEvidence(conversation) {
  return BSUID_PATTERN.test(conversation?.senderId || '');
}

// Single normalization point for the whole module (guard for backend#286, see #213).
// senderIdType is mis-stamped at the source (backend#288): as of 2026-08-13,
// 14,608 production clients are labeled 'userId' and only 1,526 of them carry a
// BSUID in senderId; the rest sit over a phone or a Messenger PSID. Once backend#286 starts projecting the field over REST, every
// helper here would believe the label.
//
// So: a 'userId' label with no evidence behind it is treated as if the label had
// not arrived at all — which is exactly today's REST state. Callers must read
// this instead of conversation.senderIdType, never the raw field.
function normalizedSenderIdType(conversation) {
  const senderIdType = conversation?.senderIdType;
  if (senderIdType === 'userId' && !hasUserIdEvidence(conversation)) {
    return undefined;
  }
  return senderIdType;
}

export function isUserIdIdentity(conversation) {
  if (!conversation) return false;
  const senderIdType = normalizedSenderIdType(conversation);
  if (senderIdType === 'userId') return true;
  // Fallback for partially hydrated clients (conversation list / Pusher events)
  // where senderIdType is missing. It reads the SAME evidence as above — the
  // BSUID shape of senderId — so both paths ask one identical question and can
  // never diverge; it only adds the extra requirement that no phoneNumber is
  // present. It fires only when the normalized label is ABSENT: an unrecognized
  // label value does not trigger it.
  return (
    !senderIdType &&
    !conversation.phoneNumber &&
    hasUserIdEvidence(conversation)
  );
}

// Returns the BSUID to display for an established userId identity. It validates
// the SHAPE of whatsappUserId instead of trusting it for being non-empty: that
// field is enrichment and can carry a phone or a copied PSID (see hasUserIdEvidence),
// so trusting it would print a phone number labelled as a WhatsApp identity.
// Since isUserIdIdentity only passes when senderId matches the pattern, the
// fallback always yields a well-formed BSUID.
export function getUserIdOrNull(conversation) {
  if (!isUserIdIdentity(conversation)) return null;
  if (BSUID_PATTERN.test(conversation.whatsappUserId || '')) {
    return conversation.whatsappUserId;
  }
  return conversation.senderId || null;
}

export function getDisplayIdentifier(conversation) {
  if (conversation.phoneNumber) {
    return conversation.phoneNumber;
  }
  if (conversation.whatsappUsername) {
    return conversation.whatsappUsername;
  }
  if (normalizedSenderIdType(conversation) === 'userId') {
    return 'Usuario de WhatsApp';
  }
  return conversation.senderId;
}

// An evidenced userId identity always wins: a BSUID client may carry an
// enriched/stale phoneNumber, and it must never fall into the phone branch
// (flag + phone + edit button). Note isUserIdIdentity's BSUID fallback only
// fires without a phoneNumber, so legacy phone-only clients are unaffected.
// Conversely, a 'userId' label with no evidence normalizes away, so those
// clients keep reaching the phone branch exactly as they do today.
export function isPhoneNumber(conversation) {
  if (!conversation || isUserIdIdentity(conversation)) return false;
  const senderIdType = normalizedSenderIdType(conversation);
  return (
    !!conversation.phoneNumber ||
    senderIdType === 'phoneNumber' ||
    !senderIdType
  );
}

export function getPhoneOrNull(conversation) {
  if (!conversation || isUserIdIdentity(conversation)) return null;
  if (conversation.phoneNumber) return conversation.phoneNumber;
  const senderIdType = normalizedSenderIdType(conversation);
  if (senderIdType === 'phoneNumber' || !senderIdType) {
    return conversation.senderId || null;
  }
  return null;
}

export function getWhatsAppLink(conversation) {
  const phone = getPhoneOrNull(conversation);
  if (!phone) return null;
  const cleanPhone = phone.replace(/\D/g, '');
  return `https://wa.me/${cleanPhone}`;
}
